import React, { useCallback, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import Navbar from './Navbar';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';

const MainLayout = ({ themeMode, onToggleTheme }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { showNotification } = useNotification();
  const idleTimerRef = useRef(null);

  const resetIdleTimer = useCallback(() => {
    if (!user) return;
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(async () => {
      try {
        await logout();
        showNotification({
          message: 'You were signed out after 5 minutes of inactivity.',
          severity: 'info',
          autoHideDuration: 6000,
        });
      } finally {
        navigate('/login', { replace: true, state: { sessionExpired: true } });
      }
    }, 5 * 60 * 1000);
  }, [logout, navigate, showNotification, user]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousemove', 'keydown', 'touchstart', 'scroll'];
    const handleActivity = () => resetIdleTimer();

    resetIdleTimer();
    events.forEach(event => window.addEventListener(event, handleActivity));

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      events.forEach(event => window.removeEventListener(event, handleActivity));
    };
  }, [resetIdleTimer, user]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <Navbar themeMode={themeMode} onToggleTheme={onToggleTheme} />
      <Box
        component="main"
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          pt: 3,
          pb: isMobile ? `calc(${theme.spacing(8)} + env(safe-area-inset-bottom))` : 4,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
